import { Grid, Link, Paper } from "@mui/material";
import { type FC, type ReactElement, useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { UUID } from "../../../../utils/uuid";
import {
  MomentAccordion,
  type MomentAccordionProps,
} from "../../../generic/components/moment-accordion/moment-accordion";
import { NextSendsWrapper } from "../../../schedule/components/next-sends";
import { ScheduleEnabler } from "../../../schedule/components/schedule-enabler";
import { isScheduled } from "../../../schedule/types/utils/is-scheduled";
import type { ProgramsState } from "../../hooks/use-programs";
import type { ConnectionCategory } from "../../hooks/use-connections";
import type { MomentFormMoment } from "../../types/moment-form-moment";

interface ScheduleAccordionProps extends Pick<
  MomentAccordionProps,
  "open" | "setOpen" | "onConfirm" | "sx"
> {
  formDisabled: boolean;
  lastFormAction?: "draft" | "publish";
  reloadMergeTags: () => void;
  programs: ProgramsState;
  connectionCategories: ConnectionCategory[];
  restrictedSegments: UUID[];
}

export const ScheduleAccordion: FC<ScheduleAccordionProps> = ({
  open,
  setOpen,
  formDisabled,
  onConfirm,
  reloadMergeTags,
  lastFormAction,
  programs,
  connectionCategories,
  restrictedSegments,
  sx,
}) => {
  const [icon, setIcon] = useState<ReactElement>();

  const {
    control,
    trigger,
    getValues,
    formState: { isSubmitted: showValidationErrors, errors },
  } = useFormContext<MomentFormMoment>();
  useWatch({
    control,
    name: ["schedule.event", "immediate", "trigger_survey_id"],
  });
  const [event, immediate, triggerSurveyId] = getValues([
    "schedule.event",
    "immediate",
    "trigger_survey_id",
  ]);

  const onSubmit = useCallback(async (): Promise<void> => {
    await trigger("schedule");
    void onConfirm?.()?.catch(console.error);
  }, [onConfirm, trigger]);

  const scheduleType = immediate
    ? "immediate"
    : triggerSurveyId
      ? "survey-completed"
      : isScheduled(event)
        ? "scheduled"
        : "one-off";

  return (
    <MomentAccordion
      id="whenAccordion"
      summaryIcon={icon}
      title="When"
      subtitle="When do you want to send it?"
      index={3}
      open={open}
      setOpen={setOpen}
      disabled={formDisabled}
      sx={{ minWidth: "100%", mb: 3, ...sx }}
      onConfirm={onSubmit}
      valid={{ valid: !errors.schedule, lastFormAction }}
    >
      <Grid container spacing={7}>
        <Grid size={{ md: 4 }}>
          <h2>When</h2>
          {scheduleType === "survey-completed" ? (
            <>
              Choose a survey to trigger this Communication. It will send automatically when a
              recipient completes the selected survey.
            </>
          ) : (
            <>
              Decide when to send your Communication. Decide from various options including
              scheduled or event based triggers. &nbsp;
              <Link
                href="https://help.changeengine.com/How-to-Time-and-Schedule-Moments-a534dbe6a48745ebb71c174e3b93409c"
                target="_blank"
                rel="noreferrer"
              >
                Learn more about scheduling logic.
              </Link>
            </>
          )}
          <Paper
            variant="elevation"
            elevation={16}
            sx={{
              minHeight: immediate ? "100px" : "200px",
              mt: 2,
              bgcolor: "background.contrast",
              p: 2,
            }}
          >
            <NextSendsWrapper />
          </Paper>
        </Grid>
        <Grid size={{ md: 8 }}>
          <ScheduleEnabler
            variant="standard"
            sx={{ m: 1, minWidth: 120 }}
            initialScheduleType={scheduleType}
            setIcon={setIcon}
            disabled={formDisabled}
            reloadMergeTags={reloadMergeTags}
            showValidationErrors={showValidationErrors}
            programs={programs}
            connectionCategories={connectionCategories}
            restrictedSegments={restrictedSegments}
          />
        </Grid>
      </Grid>
    </MomentAccordion>
  );
};
